<script setup lang="ts">
  import { Modal } from '@travelata-libs/ui-kit';
  import { ref, computed } from 'vue';
  import type { LeadBannerPayload } from './LeadBanner.types';
  import LeadBannerForm from './LeadBannerForm.vue';

  export interface LeadBannerModalProps {
    open: boolean;
    mobile: boolean;
    modelValue: LeadBannerPayload;
    buttonText: string;
    userAgreementUrl: string;
    privacyPolicyUrl: string;
    dataProcessingUrl: string;
    loading?: boolean;
    closable?: boolean;
    zIndex?: number;
    centeredButton?: boolean;
    overlayClosable?: boolean;
  }

  interface LeadBannerModalEmits {
    (e: 'update:modelValue', value: LeadBannerPayload): void;
    (e: 'submit', payload: LeadBannerPayload): void;
    (e: 'focus'): void;
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<LeadBannerModalProps>(), {
    loading: false,
    closable: false,
    zIndex: 5,
    centeredButton: false,
    overlayClosable: true,
  });

  const emit = defineEmits<LeadBannerModalEmits>();

  const model = computed({
    get: () => props.modelValue,
    set: (value) => {
      emit('update:modelValue', value);
    },
  });

  const formRef = ref<InstanceType<typeof LeadBannerForm> | null>(null);

  defineExpose({
    focusName: () => formRef.value?.focusName(),
    blur: () => formRef.value?.blur(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <Modal
    v-if="open"
    class="tl-lead-banner-modal"
    :mobile="mobile"
    :closable="closable"
    :overlay-closable="overlayClosable"
    :z-index="zIndex"
    @close="emit('close')"
  >
    <slot />

    <LeadBannerForm
      ref="formRef"
      v-model="model"
      class="tl-lead-banner-modal__form"
      data-test-id="lead-banner-modal-form"
      :mobile="mobile"
      :button-text="buttonText"
      :loading="loading"
      :centered-button="centeredButton"
      :user-agreement-url="userAgreementUrl"
      :privacy-policy-url="privacyPolicyUrl"
      :data-processing-url="dataProcessingUrl"
      @submit="emit('submit', $event)"
      @focus="emit('focus')"
    />
  </Modal>
</template>
